import React, { useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { SignIn, FloppyDisk } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { trackEvent } from "@/lib/analytics";

// Modal shown when an anonymous user tries a mutation that requires
// persistent storage (toggle task, add custom item, save budget, save profile).
// Non-blocking: it only interrupts the specific action, not page navigation.
export default function AuthPromptModal({ open, onOpenChange, reason, afterPath = "/organizza" }) {
  const navigate = useNavigate();
  const { t } = useApp();
  useEffect(() => {
    if (open) trackEvent("Auth Prompt Opened", { reason: reason || "unspecified" });
  }, [open, reason]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="auth-prompt-modal" className="max-w-md">
        <DialogHeader>
          <div className="h-12 w-12 rounded-2xl bg-terracotta/10 flex items-center justify-center mb-3">
            <FloppyDisk size={26} weight="duotone" className="text-terracotta" />
          </div>
          <DialogTitle className="font-display font-black text-2xl text-ink tracking-tight text-left">
            {t.common.authPromptTitle}
          </DialogTitle>
          <DialogDescription className="text-ink-soft font-sans leading-relaxed text-left pt-1">
            {t.common.authPromptBody}
          </DialogDescription>
        </DialogHeader>
        {reason && (
          <p
            data-testid="auth-prompt-reason"
            className="text-xs text-ink-soft/80 font-sans italic border-l-2 border-terracotta/40 pl-3 mt-1"
          >
            {reason}
          </p>
        )}
        <div className="flex flex-col-reverse sm:flex-row gap-2 mt-4">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            data-testid="auth-prompt-later"
            className="flex-1 rounded-full font-sans"
          >
            {t.common.authPromptLater}
          </Button>
          <Button
            onClick={() => { trackEvent("Auth Prompt Signin Clicked"); navigate("/login", { state: { after: afterPath } }); }}
            data-testid="auth-prompt-signin"
            className="flex-1 rounded-full bg-terracotta hover:bg-terracotta-hover text-white font-sans font-semibold"
          >
            <SignIn size={16} weight="duotone" className="mr-1.5" />
            {t.common.authPromptSignin}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
